import React, { useMemo, useState } from "react";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import AdaptStepIndicator from "../../components/patterns/AdaptStepIndicator";
import IdeasSectionHeader from "../../components/patterns/IdeasSectionHeader";
import IdeaCard from "../../components/patterns/IdeaCard";
import IdeasEmptyState from "../../components/patterns/IdeasEmptyState";
import { GENERATING_IDEAS_MESSAGES } from "../../utils/patterns";

/**
 * Generate Ideas step of the Adapt flow.
 */
const AdaptGenerateIdeas = ({
  baseUrlSlash,
  pattern,
  patternTypeLabel,
  patternType,
  brand,
  ideas,
  brandIdeaPrompt,
  brandPromotionOverride,
  brandNumIdeas,
  onBrandIdeaPromptChange,
  onBrandPromotionChange,
  onBrandNumIdeasChange,
  onGenerateIdeas,
  onGenerateScript,
  generatingForBrand,
  generatingScriptForIdea,
  scriptPromptsByIdea,
  onScriptPromptChange,
  scriptPromotionByIdea,
  onScriptPromotionChange,
  onToggleStar,
  tweakOpenForIdea,
  onTweakOpenChange,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const hasIdeas = ideas.length > 0;
  const isGenerating = generatingForBrand === brand?.hash;
  const generatingMessage = useMemo(
    () => (isGenerating ? GENERATING_IDEAS_MESSAGES[Math.floor(Math.random() * GENERATING_IDEAS_MESSAGES.length)] : null),
    [isGenerating]
  );

  if (!brand?.hash) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <AdaptStepIndicator
          baseUrlSlash={baseUrlSlash}
          currentStep="generate"
          brand={brand}
        />
      </div>

      {hasIdeas ? (
        <IdeasSectionHeader
          ideaCount={ideas.length}
          onGenerateIdeas={onGenerateIdeas}
          isGenerating={isGenerating}
          promotionValue={brandPromotionOverride}
          onPromotionChange={onBrandPromotionChange}
          promptValue={brandIdeaPrompt}
          onPromptChange={onBrandIdeaPromptChange}
          numIdeas={brandNumIdeas ?? 3}
          onNumIdeasChange={onBrandNumIdeasChange}
          settingsOpen={settingsOpen}
          onSettingsToggle={() => setSettingsOpen((prev) => !prev)}
          generateButtonLabel={`Generate ${brandNumIdeas ?? 3} more idea${(brandNumIdeas ?? 3) === 1 ? "" : "s"}`}
          usePrimaryButton={false}
        />
      ) : null}

      {isGenerating ? (
        <div className="py-8 flex justify-center">
          <OrbitalLoader
            size={72}
            text={generatingMessage}
            special
          />
        </div>
      ) : hasIdeas ? (
        <div className="space-y-4">
          {ideas.map((idea) => (
            <IdeaCard
              key={idea.hash}
              idea={idea}
              selectedBrandHash={brand.hash}
              scriptPromptValue={scriptPromptsByIdea[idea.hash]}
              scriptPromotionValue={scriptPromotionByIdea[idea.hash]}
              onScriptPromptChange={onScriptPromptChange}
              onScriptPromotionChange={onScriptPromotionChange}
              isTweakOpen={tweakOpenForIdea === idea.hash}
              onTweakOpenChange={onTweakOpenChange}
              onToggleStar={onToggleStar}
              onSave={onToggleStar}
              onGenerateScript={onGenerateScript}
              isGeneratingScript={generatingScriptForIdea === idea.hash}
              baseUrlSlash={baseUrlSlash}
            />
          ))}
        </div>
      ) : (
        <IdeasEmptyState
          pattern={pattern}
          patternTypeLabel={patternTypeLabel}
          patternType={patternType}
          brand={brand}
          onGenerateIdeas={onGenerateIdeas}
          onSettingsToggle={() => setSettingsOpen((prev) => !prev)}
          isGenerating={isGenerating}
          promotionValue={brandPromotionOverride}
          onPromotionChange={onBrandPromotionChange}
          promptValue={brandIdeaPrompt}
          onPromptChange={onBrandIdeaPromptChange}
          numIdeas={brandNumIdeas ?? 3}
          onNumIdeasChange={onBrandNumIdeasChange}
          settingsOpen={settingsOpen}
        />
      )}
    </div>
  );
};

export default AdaptGenerateIdeas;
